import { useState } from 'react';
import { PortalDsBadge, PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';
import type { SurveySegment } from 'src/types/focus-group.types';
import {
  formatSegmentShare,
  summariseDefiningAnswers,
  summariseDemographics,
} from './focus-group-utils';

interface SegmentCardProps {
  segment: SurveySegment;
  totalAiRespondents: number;
  questions: CombinedQuestionStats[];
  onOpenChat: () => void;
}

export function SegmentCard({
  segment,
  totalAiRespondents,
  questions,
  onOpenChat,
}: SegmentCardProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const persona = segment.persona;
  const definingAnswers = summariseDefiningAnswers(segment, questions);
  const demographicSummary = summariseDemographics(segment.demographic_profile);

  return (
    <article className={cn(portalDs.card, portalDs.cardPadding, 'flex h-full flex-col')}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className={portalDs.headingH3}>
            {persona?.name ?? segment.label}
          </h3>
          <p className={cn(portalDs.bodySm, 'mt-1')}>
            {persona?.descriptor ?? segment.hypothesis}
          </p>
        </div>
        <PortalDsBadge variant="blue" pill>
          {formatSegmentShare(segment.size, totalAiRespondents)}
        </PortalDsBadge>
      </div>

      {definingAnswers.length > 0 ? (
        <div className="mb-4">
          <h4 className={portalDs.sectionLabel}>
            Defining answers
          </h4>
          <ul className={cn(portalDs.bodySm, 'mt-2 space-y-1 text-[#0D0F1A]')}>
            {definingAnswers.map((line) => (
              <li key={line}>
                {line}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {demographicSummary.length > 0 ? (
        <div className="mb-4">
          <h4 className={portalDs.sectionLabel}>
            Demographics
          </h4>
          <p className={cn(portalDs.bodySm, 'mt-2 text-[#0D0F1A]')}>
            {demographicSummary.join(' · ')}
          </p>
        </div>
      ) : null}

      {persona?.summary_paragraph ? (
        <p className={cn(portalDs.bodySm, 'mb-4 text-[#0D0F1A]')}>
          {persona.summary_paragraph}
        </p>
      ) : null}

      {persona ? (
        <div className="mb-4">
          <PortalDsButton
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-0"
            onClick={() => setDetailsOpen((open) => !open)}
            aria-expanded={detailsOpen}
          >
            {detailsOpen ? 'Hide traits and motivations' : 'Show traits and motivations'}
          </PortalDsButton>
          {detailsOpen ? (
            <div className={cn(portalDs.bodySm, 'mt-2 space-y-3 text-[#0D0F1A]')}>
              {persona.core_traits.length > 0 ? (
                <div>
                  <div className="font-semibold">
                    Core traits
                  </div>
                  <p>
                    {persona.core_traits.join(', ')}
                  </p>
                </div>
              ) : null}
              {persona.motivations.length > 0 ? (
                <div>
                  <div className="font-semibold">
                    Motivations
                  </div>
                  <p>
                    {persona.motivations.join(', ')}
                  </p>
                </div>
              ) : null}
              {persona.decision_style ? (
                <div>
                  <div className="font-semibold">
                    Decision style
                  </div>
                  <p>
                    {persona.decision_style}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {persona?.tensions?.length ? (
        <div
          className={cn(portalDs.alertBase, portalDs.alertWarning, 'mb-4 px-4 py-3')}
          data-testid="segment-tensions"
        >
          <div>
            <h4 className={portalDs.alertTitle}>
              Internal tensions
            </h4>
            <ul className={cn(portalDs.alertBody, 'mt-2 space-y-1')}>
              {persona.tensions.map((tension) => (
                <li key={tension}>
                  {tension}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="mt-auto pt-2">
        <PortalDsButton
          type="button"
          variant="accent"
          onClick={onOpenChat}
        >
          Interview segment
        </PortalDsButton>
      </div>
    </article>
  );
}
